import { Button, Card, Descriptions, Modal, message, Spin } from "antd";
import React, { Fragment } from "react";
import { NavLink, RouteComponentProps } from "react-router-dom";
import EntitledSection from "../components/EntitledSection";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import entitlements from "../config/entitlements";
import { EconomicIndicator } from "../helpers/shared-interfaces";
import { request } from "../helpers/utilities";
import { AxiosResponse } from "axios";
import {
  DELETE_ECONOMIC_INDICATOR,
  GET_ECONOMIC_INDICATOR,
} from "../gql/economicIndicators";

interface RouteParams {
  id: string;
}

interface State {
  isLoading: boolean;
  id: string | number;
  showDialog: boolean;
  economicIndicator?: EconomicIndicator;
}

function ModalWrapper(
  showDialog: boolean,
  deleteEconomicIndicator: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteEconomicIndicator}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this economic indicator?</div>
    </Modal>
  );
}

export default class ViewEconomicIndicator extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      isLoading: true,
      id: "",
      showDialog: false,
      economicIndicator: undefined,
    };
    this.deleteEconomicIndicator = this.deleteEconomicIndicator.bind(this);
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
  }

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getEconomicIndicator();
    });
  }

  getEconomicIndicator = (): void => {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_ECONOMIC_INDICATOR,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            const economicIndicator = data.data?.economicIndicator;
            this.setState({
              economicIndicator: economicIndicator,
            });
          } else {
            message.error("Failed to get economic indicator data");
          }
        });
      },
    );
  };

  deleteEconomicIndicator(): void {
    const { id } = this.state;

    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: DELETE_ECONOMIC_INDICATOR,
          variables: { id },
        })
          .then(({ data }: AxiosResponse<any>): void => {
            this.setState({ isLoading: false, showDialog: false });
            if (!data.errors && data.data.deleteEconomicIndicator.result) {
              message.success("Economic indicator deleted successfuly");
              this.redirectToAllEconomicIndicatorsPage();
            } else {
              message.error("Failed to delete economic indicator");
            }
          })
          .catch((): void => {
            this.setState({ isLoading: false, showDialog: false });
          });
      },
    );
  }

  toggleDeleteConfirmationModal(): void {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  }

  redirectToAllEconomicIndicatorsPage(): void {
    this.props.history.push("/economic-indicators");
  }

  render(): JSX.Element | null {
    const { economicIndicator, isLoading, id, showDialog } = this.state;
    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            <div>
              {ModalWrapper(
                showDialog,
                this.deleteEconomicIndicator,
                this.toggleDeleteConfirmationModal,
              )}
              <Descriptions
                extra={
                  <div>
                    <EntitledSection
                      entitlement={entitlements.EconomicIndicators.edit}
                    >
                      <NavLink to={`/economic-indicators/${id}/edit`}>
                        <Button
                          icon={<EditOutlined />}
                          size="large"
                          style={{
                            backgroundColor: "  rgb(29, 55, 100)",
                            color: "white",
                            marginRight: "10px",
                          }}
                          data-cy="test-economic-indicators-edit-button"
                        >
                          Edit
                        </Button>
                      </NavLink>
                    </EntitledSection>
                    <EntitledSection
                      entitlement={entitlements.EconomicIndicators.delete}
                    >
                      <Button
                        icon={<DeleteOutlined />}
                        size="large"
                        onClick={this.toggleDeleteConfirmationModal}
                        data-cy="test-economic-indicators-delete-button"
                      >
                        Delete
                      </Button>
                    </EntitledSection>
                  </div>
                }
              >
                <Descriptions.Item label={<b>Date</b>} key="date">
                  {economicIndicator?.date}
                </Descriptions.Item>
                <Descriptions.Item
                  label={<b>Inflation Rate %</b>}
                  key="inflation"
                >
                  {economicIndicator?.inflation}
                </Descriptions.Item>
                <Descriptions.Item
                  label={<b>Treasury Bill Rate %</b>}
                  key="tbill"
                >
                  {economicIndicator?.tbill}
                </Descriptions.Item>
              </Descriptions>
            </div>
          </Fragment>
        </Card>
      </Spin>
    );
  }
}
